import React from 'react';

import {
    NUTRITION_FIELD_KEYS,
    NUTRITION_UNIT_OPTIONS,
    defaultUnitForNutrient,
    formatNutritionLabel,
    parseQuantityUnit,
} from './foodNutrition';

const EMPTY_ROW = () => ({
    nutrient: '',
    quantity: '',
    unit: 'gm',
});

function normalizeNutritionRow(row) {
    if (!row || typeof row !== 'object') return EMPTY_ROW();
    const nutrient = String(row.nutrient ?? row.nutrientType ?? '').trim().toLowerCase();
    return {
        nutrient,
        quantity: row.quantity != null ? String(row.quantity).trim() : '',
        unit: String(row.unit ?? defaultUnitForNutrient(nutrient)).trim() || defaultUnitForNutrient(nutrient),
    };
}

function nutrientOptionsForRow(rows, rowIndex) {
    const used = new Set(
        rows
            .map((row, index) => (index === rowIndex ? '' : normalizeNutritionRow(row).nutrient))
            .filter(Boolean)
    );
    return NUTRITION_FIELD_KEYS.filter((key) => !used.has(key));
}

/**
 * Per-serving nutrition editor (MYEATS and JREATS menu items).
 */
export default function NutritionEditor({ value, onChange, disabled = false }) {
    const rows = Array.isArray(value) ? value.map(normalizeNutritionRow) : [];

    const updateRow = (index, patch) => {
        const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
        onChange?.(next);
    };

    const addRow = () => {
        const available = nutrientOptionsForRow(rows, -1);
        const nextNutrient = available[0] ?? '';
        onChange?.([
            ...rows,
            {
                ...EMPTY_ROW(),
                nutrient: nextNutrient,
                unit: defaultUnitForNutrient(nextNutrient),
            },
        ]);
    };

    const removeRow = (index) => {
        onChange?.(rows.filter((_, i) => i !== index));
    };

    const canAddRow = rows.length < NUTRITION_FIELD_KEYS.length;

    return (
        <div className="nutrition-editor">
            <div className="nutrition-editor__header">
                <label className="nutrition-editor__title">Nutrition (per serving)</label>
                <p className="nutrition-editor__hint">
                    Add protein, carbs, fat, fiber, and sugar with quantity and unit for each nutrient.
                </p>
            </div>
            {rows.length === 0 ? (
                <p className="nutrition-editor__empty">No nutrition values yet. Add a row for each nutrient.</p>
            ) : (
                <div className="nutrition-editor__table-wrap">
                    <table className="nutrition-editor__table">
                        <thead>
                            <tr>
                                <th>Nutrient</th>
                                <th>Quantity</th>
                                <th>Unit</th>
                                <th aria-label="Remove" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                const options = nutrientOptionsForRow(rows, index);
                                const selectedNutrient = normalizeNutritionRow(row).nutrient;
                                const selectOptions =
                                    selectedNutrient && !options.includes(selectedNutrient)
                                        ? [selectedNutrient, ...options]
                                        : options;

                                return (
                                    <tr key={`nutrition-row-${index}`}>
                                        <td>
                                            <div className="inventory-native-select-wrap">
                                                <select
                                                    className="add-menu-form-field inventory-select-native nutrition-editor__select"
                                                    value={selectedNutrient}
                                                    disabled={disabled}
                                                    onChange={(e) => {
                                                        const nextNutrient = e.target.value;
                                                        updateRow(index, {
                                                            nutrient: nextNutrient,
                                                            unit: defaultUnitForNutrient(nextNutrient),
                                                        });
                                                    }}
                                                >
                                                    <option value="">Select nutrient</option>
                                                    {selectOptions.map((key) => (
                                                        <option key={key} value={key}>
                                                            {formatNutritionLabel(key)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                min="0"
                                                step="any"
                                                className="add-menu-form-field nutrition-editor__input"
                                                value={row.quantity}
                                                disabled={disabled}
                                                placeholder="10"
                                                onChange={(e) =>
                                                    updateRow(index, { quantity: e.target.value })
                                                }
                                            />
                                        </td>
                                        <td>
                                            <div className="inventory-native-select-wrap">
                                                <select
                                                    className="add-menu-form-field inventory-select-native nutrition-editor__select"
                                                    value={row.unit || 'gm'}
                                                    disabled={disabled}
                                                    onChange={(e) =>
                                                        updateRow(index, { unit: e.target.value })
                                                    }
                                                >
                                                    {NUTRITION_UNIT_OPTIONS.map((unit) => (
                                                        <option key={unit} value={unit}>
                                                            {unit}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="nutrition-editor__remove-btn"
                                                disabled={disabled}
                                                onClick={() => removeRow(index)}
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            <button
                type="button"
                className="nutrition-editor__add-btn"
                disabled={disabled || !canAddRow}
                onClick={addRow}
            >
                + Add nutrition row
            </button>
        </div>
    );
}

export function normalizeNutritionRowsForSubmit(rows) {
    if (!Array.isArray(rows)) return [];
    return rows
        .map((row) => normalizeNutritionRow(row))
        .filter((row) => row.nutrient && row.quantity !== '' && row.quantity != null);
}

export function parseNutritionRowsFromItem(item) {
    const raw = item?.nutrition ?? item?.Nutrition;
    if (Array.isArray(raw)) {
        return normalizeNutritionRowsForSubmit(raw).map((row) => ({ ...row }));
    }
    const map = raw && typeof raw === 'object' ? raw : {};
    const rows = [];
    NUTRITION_FIELD_KEYS.forEach((key) => {
        const parsed = parseQuantityUnit(map[key], defaultUnitForNutrient(key));
        if (parsed.quantity !== '' && parsed.quantity != null) {
            const numeric = Number(parsed.quantity);
            if (Number.isFinite(numeric) && numeric !== 0) {
                rows.push({
                    nutrient: key,
                    quantity: parsed.quantity,
                    unit: parsed.unit,
                });
            }
        }
    });
    const hasCaloriesRow = rows.some((row) => row.nutrient === 'calories');
    const legacyCalories = item?.calories ?? item?.Calories;
    if (!hasCaloriesRow && legacyCalories != null && String(legacyCalories).trim() !== '') {
        const parsed = parseQuantityUnit(legacyCalories, 'kcal');
        const numeric = Number(parsed.quantity);
        if (parsed.quantity !== '' && Number.isFinite(numeric) && numeric !== 0) {
            rows.push({
                nutrient: 'calories',
                quantity: parsed.quantity,
                unit: parsed.unit || 'kcal',
            });
        }
    }
    return rows;
}
